import {
  Button,
  FlexBox,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { extractTitleFromTranslations } from 'lib-knowledge-management-common/utils/translations';
import { ModalWrapper, useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useCallback } from 'react';

import { useArticleCacheFetch } from 'hooks/useArticleCacheFetch';
import { useArticleDelete } from 'hooks/useArticleDelete';

export function ArticleDeleteModal({
  articleId,
  onDelete,
}: {
  articleId: string;
  onDelete?: () => void;
}): JSX.Element {
  const { hideModal } = useModal();
  const { errorNotify, successNotify } = useNotification();

  // Don't want this to re-run after article is deleted.
  const article = useArticleCacheFetch({
    articleId,
  });

  const articleTitle = article && extractTitleFromTranslations(article.titles);

  const [deleteFn, { loading }] = useArticleDelete();

  const deleteArticle = useCallback(() => {
    async function handleArticleDelete() {
      try {
        await deleteFn({ articleId });
        onDelete && onDelete();
        hideModal();
        successNotify({
          title: 'Article Deleted',
          text: `"${articleTitle}" has been deleted`,
        });
      } catch (_error) {
        errorNotify({
          text: `"${articleTitle}" could not be deleted`,
        });
      }
    }
    handleArticleDelete();
  }, [
    articleId,
    hideModal,
    articleTitle,
    deleteFn,
    successNotify,
    errorNotify,
    onDelete,
  ]);

  return (
    <ModalWrapper>
      <ModalHead title="Delete Article" onClose={hideModal}></ModalHead>
      <ModalBody>
        <Text.Paragraph>
          Are you sure you wish to delete{' '}
          <strong>&quot;{articleTitle}&quot;</strong>?
        </Text.Paragraph>
        <FlexBox justifyContent="flex-end" gap="100">
          <Button disabled={loading} variant="tertiary" onClick={hideModal}>
            Cancel
          </Button>
          <Button disabled={loading} onClick={deleteArticle}>
            Delete
          </Button>
        </FlexBox>
      </ModalBody>
    </ModalWrapper>
  );
}
